import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabase/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import "./AddProduct.css";
import FloatingSidebarButton from "../../components/FloatingSidebarButton/FloatingSidebarButton";
import { FaArrowLeft, FaPlus, FaImage, FaDollarSign, FaInfoCircle } from "react-icons/fa";

// Currency flags
import aeflag from "../../assets/images/flags/ae_flag.png";
import frflag from "../../assets/images/flags/fr_flag.png";
import lbflag from "../../assets/images/flags/lb_flag.svg";
import qaflag from "../../assets/images/flags/qa_flag.svg";
import saflag from "../../assets/images/flags/saudi_flag.webp";
import syflag from "../../assets/images/flags/syria_flag.webp";
import usflag from "../../assets/images/flags/us_flag.png";

const flagMap = {
  AED: aeflag,
  EUR: frflag,
  LBP: lbflag,
  QAR: qaflag,
  SAR: saflag,
  SYP: syflag,
  USD: usflag,
};

export default function AddProduct() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { translations: tRaw } = useLanguage();
  const t = { ...tRaw?.addProduct };

  const [productName, setProductName] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const [images, setImages] = useState([]);
  const [priceMode, setPriceMode] = useState("coins"); // "coins" or "usd"
  const [priceAmount, setPriceAmount] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [winiToUsdRate, setWiniToUsdRate] = useState(null);
  const [discountPercentage, setDiscountPercentage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const fileInputRef = useRef(null);
  const currencyDropdownRef = useRef(null);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [currencySearch, setCurrencySearch] = useState("");

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      if (!currentUser?.id) return;

      try {
        const { data, error } = await supabase
          .from("product_categories")
          .select("*")
          .eq("user_id", currentUser.id)
          .eq("is_active", true)
          .order("sort_order", { ascending: true });

        if (error) {
          console.error("Error fetching categories:", error);
        } else {
          setCategories(data || []);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };

    fetchCategories();
  }, [currentUser?.id]);

  // Fetch currencies and Wini rate
  useEffect(() => {
    const fetchCurrencyData = async () => {
      try {
        // Fetch WiniCoin rate
        const { data: appSettings, error: settingsError } = await supabase
          .from("app_settings")
          .select("wini_to_usd_rate")
          .limit(1)
          .single();

        if (!settingsError && appSettings) {
          setWiniToUsdRate(appSettings.wini_to_usd_rate || null);
        }

        // Fetch currencies
        const { data: currenciesData, error: currenciesError } = await supabase
          .from("currencies")
          .select("*")
          .order("iso");

        if (!currenciesError && currenciesData) {
          const list = currenciesData
            .filter((c) => c.rate && c.name)
            .map((c) => ({
              code: c.iso,
              name: c.name,
              rate: c.rate,
              flag: flagMap[c.iso] || usflag,
            }))
            .sort((a, b) => a.code.localeCompare(b.code));

          setCurrencies(list);
          if (list.length > 0 && !selectedCurrency) {
            setSelectedCurrency(list.find((c) => c.code === "USD") || list[0]);
          }
        }
      } catch (err) {
        console.error("Error fetching currency data:", err);
      }
    };

    fetchCurrencyData();
  }, []);

  // Handle click outside currency dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(event.target)) {
        setShowCurrencyDropdown(false);
        setCurrencySearch("");
      }
    };

    if (showCurrencyDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showCurrencyDropdown]);

  // Calculate winicoins from currency
  const calculateWinicoinsFromCurrency = (currencyAmount, currencyRate) => {
    if (!currencyAmount || !currencyRate || !winiToUsdRate) return 0;
    const usd = parseFloat(currencyAmount) / parseFloat(currencyRate);
    return usd / winiToUsdRate;
  };

  // Calculate final price in coins
  const finalPriceInCoins = useMemo(() => {
    if (!priceAmount) return 0;

    if (priceMode === "coins") {
      return parseFloat(priceAmount) || 0;
    } else {
      if (!selectedCurrency) return 0;
      return calculateWinicoinsFromCurrency(priceAmount, selectedCurrency.rate);
    }
  }, [priceAmount, priceMode, selectedCurrency, winiToUsdRate]);

  // Calculate USD equivalent
  const usdEquivalent = useMemo(() => {
    if (!finalPriceInCoins || !winiToUsdRate) return 0;
    return finalPriceInCoins * winiToUsdRate;
  }, [finalPriceInCoins, winiToUsdRate]);

  // Handle image selection
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length === 0) {
      alert("Please select image files only.");
      return;
    }

    const newImages = imageFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setImages((prev) => [...prev, ...newImages]);
  };

  // Remove image
  const handleRemoveImage = (index) => {
    setImages((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      // Revoke object URLs to prevent memory leaks
      URL.revokeObjectURL(prev[index].preview);
      return updated;
    });
  };

  // Format number with commas
  const formatNumber = (num) => {
    if (!num) return "";
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Filter currencies based on search
  const filteredCurrencies = currencies.filter((currency) => {
    if (!currencySearch.trim()) return true;
    const search = currencySearch.toLowerCase();
    return (
      currency.code.toLowerCase().includes(search) ||
      currency.name.toLowerCase().includes(search)
    );
  });

  // Handle form submission
  const handleSubmit = async () => {
    if (isSubmitting) return;

    // Validation
    if (!productName.trim()) {
      setError("Product name is required");
      return;
    }

    if (!finalPriceInCoins || finalPriceInCoins <= 0) {
      setError("Please enter a valid price");
      return;
    }

    if (images.length === 0) {
      setError("Please add at least one product image");
      return;
    }

    if (!currentUser?.id) {
      setError("You must be logged in to create a product");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      // Upload images to product-media bucket
      const uploadedImageUrls = [];
      // Use a numeric timestamp so filenames follow product_<number>_<index> pattern
      const prefix = Date.now().toString();

      for (let i = 0; i < images.length; i++) {
        const image = images[i];
        const ext = image.file.name.split(".").pop() || "jpg";
        // Example: product_1758207258349_0.jpg
        const filename = `product_${prefix}_${i}.${ext}`;
        const path = `${currentUser.id}/${filename}`;

        const { error: uploadError } = await supabase.storage
          .from("product-media")
          .upload(path, image.file, {
            cacheControl: "3600",
            upsert: false,
            contentType: image.file.type || "image/jpeg",
          });

        if (uploadError) {
          throw new Error(`Failed to upload image: ${uploadError.message}`);
        }

        // Store full public URL in the products.images column
        const publicUrl = `https://dtskgpqvvxcbtcsmedrq.supabase.co/storage/v1/object/public/product-media/${path}`;
        uploadedImageUrls.push(publicUrl);
      }

      // Calculate discount
      const discount = discountPercentage
        ? parseFloat(discountPercentage)
        : null;

      // Insert product into database
      const productData = {
        user_id: currentUser.id,
        name: productName.trim(),
        description: description.trim() || null,
        category_id: categoryId || null,
        price_in_coins: finalPriceInCoins,
        images: uploadedImageUrls,
        thumbnail_url: uploadedImageUrls[0] || null,
        discount_percentage: discount && discount >= 0 && discount <= 100 ? discount : null,
        is_active: true,
        is_digital: false,
        stock_quantity: -1, // -1 means unlimited
        sold_count: 0,
        rating: 0,
        review_count: 0,
      };

      const { error: insertError } = await supabase
        .from("products")
        .insert(productData);

      if (insertError) {
        throw new Error(`Failed to create product: ${insertError.message}`);
      }

      // Clean up preview URLs
      images.forEach((img) => URL.revokeObjectURL(img.preview));

      // Success - navigate back
      alert("Product created successfully!");
      navigate(-1);
    } catch (err) {
      console.error("Error creating product:", err);
      setError(err.message || "Failed to create product. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      images.forEach((img) => URL.revokeObjectURL(img.preview));
    };
  }, []);

  const isFormValid = productName.trim() && finalPriceInCoins > 0 && images.length > 0;

  return (
    <div className="add-product-page">
      <FloatingSidebarButton userProfile={null} />
      
      {/* Header */}
      <div className="add-product-header">
        <button className="add-product-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft />
        </button>
        <h1 className="add-product-title">Add Product</h1>
        <div style={{ width: "40px" }} /> {/* Spacer for centering */}
      </div>

      <div className="add-product-container">
        {/* Product Details Section */}
        <div className="add-product-section">
          <div className="add-product-section-header">
            <FaInfoCircle className="add-product-section-icon" />
            <h2 className="add-product-section-title">Product Details</h2>
          </div>

          <div className="add-product-field">
            <label className="add-product-label">
              Product Name <span className="required">*</span>
            </label>
            <input
              type="text"
              className="add-product-input"
              placeholder="Enter product name"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
            />
          </div>

          <div className="add-product-field">
            <label className="add-product-label">Description</label>
            <textarea
              className="add-product-textarea"
              placeholder="Describe your product..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
            />
          </div>

          <div className="add-product-field">
            <label className="add-product-label">Category</label>
            <select
              className="add-product-select"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">No Category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Product Images Section */}
        <div className="add-product-section">
          <div className="add-product-section-header">
            <FaImage className="add-product-section-icon" />
            <h2 className="add-product-section-title">Product Images</h2>
          </div>

          <div className="add-product-images-grid">
            {images.map((image, index) => (
              <div key={index} className="add-product-image-preview">
                <img src={image.preview} alt={`Preview ${index + 1}`} />
                <button
                  className="add-product-remove-image"
                  onClick={() => handleRemoveImage(index)}
                >
                  ×
                </button>
              </div>
            ))}

            <div
              className="add-product-image-upload"
              onClick={() => fileInputRef.current?.click()}
            >
              <FaPlus className="add-product-upload-icon" />
              <span>Add Images</span>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={handleImageSelect}
          />
        </div>

        {/* Pricing Section */}
        <div className="add-product-section">
          <div className="add-product-section-header">
            <FaDollarSign className="add-product-section-icon" />
            <h2 className="add-product-section-title">Pricing</h2>
          </div>

          <div className="add-product-field">
            <label className="add-product-label">Price</label>
            
            {/* Price Mode Toggle */}
            <div className="add-product-price-toggle">
              <button
                className={`add-product-toggle-btn ${priceMode === "coins" ? "active" : ""}`}
                onClick={() => setPriceMode("coins")}
              >
                Coins
              </button>
              <button
                className={`add-product-toggle-btn ${priceMode === "usd" ? "active" : ""}`}
                onClick={() => setPriceMode("usd")}
              >
                USD
              </button>
            </div>

            {/* Price Input */}
            <div className="add-product-price-input-wrapper">
              {priceMode === "coins" ? (
                <>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="add-product-price-input"
                    placeholder="0"
                    value={formatNumber(priceAmount)}
                    onChange={(e) => setPriceAmount(e.target.value.replace(/[^\d.]/g, ""))}
                  />
                  <span className="add-product-price-unit">Winicoins</span>
                </>
              ) : (
                <>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="add-product-price-input"
                    placeholder="0"
                    value={formatNumber(priceAmount)}
                    onChange={(e) => setPriceAmount(e.target.value.replace(/[^\d.]/g, ""))}
                  />
                  <div className="add-product-currency-selector" ref={currencyDropdownRef}>
                    <button
                      className="add-product-currency-button"
                      onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                    >
                      {selectedCurrency ? (
                        <>
                          <img
                            src={selectedCurrency.flag}
                            alt={selectedCurrency.code}
                            className="add-product-currency-flag"
                          />
                          <span className="add-product-currency-code">{selectedCurrency.code}</span>
                        </>
                      ) : (
                        <span className="add-product-currency-placeholder">Select currency</span>
                      )}
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M6 9l6 6 6-6"
                          stroke="#666"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    {showCurrencyDropdown && (
                      <div className="add-product-currency-dropdown">
                        <div className="add-product-currency-search-wrapper">
                          <input
                            type="text"
                            className="add-product-currency-search"
                            placeholder="Search currency..."
                            value={currencySearch}
                            onChange={(e) => setCurrencySearch(e.target.value)}
                            autoFocus
                          />
                        </div>
                        <div className="add-product-currency-list">
                          {filteredCurrencies.length === 0 ? (
                            <div className="add-product-currency-empty">No currencies found</div>
                          ) : (
                            filteredCurrencies.map((currency) => (
                              <button
                                key={currency.code}
                                className={`add-product-currency-option ${
                                  selectedCurrency?.code === currency.code ? "selected" : ""
                                }`}
                                onClick={() => {
                                  setSelectedCurrency(currency);
                                  setShowCurrencyDropdown(false);
                                  setCurrencySearch("");
                                }}
                              >
                                <img
                                  src={currency.flag}
                                  alt={currency.code}
                                  className="add-product-currency-flag"
                                />
                                <div className="add-product-currency-option-text">
                                  <span className="add-product-currency-option-code">
                                    {currency.code}
                                  </span>
                                  <span className="add-product-currency-option-name">
                                    {currency.name}
                                  </span>
                                </div>
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Conversion Preview */}
            {priceMode === "usd" && priceAmount && selectedCurrency && (
              <div className="add-product-price-preview">
                ≈ {formatNumber(finalPriceInCoins.toFixed(2))} Winicoins
              </div>
            )}
          </div>

          <div className="add-product-field">
            <label className="add-product-label">Discount (Optional)</label>
            <input
              type="text"
              inputMode="numeric"
              className="add-product-input"
              placeholder="Enter discount percentage"
              value={discountPercentage}
              onChange={(e) => setDiscountPercentage(e.target.value.replace(/[^\d.]/g, ""))}
            />
          </div>

          {/* Equivalent Display */}
          {finalPriceInCoins > 0 && (
            <div className="add-product-equivalent">
              <span>Equivalent:</span>
              <span>{usdEquivalent.toFixed(2)} USD</span>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && <div className="add-product-error">{error}</div>}

        {/* Create Product Button */}
        <div className="add-product-section">
          <div className="add-product-section-header">
            <FaPlus className="add-product-section-icon" />
            <h2 className="add-product-section-title">Create Product</h2>
          </div>

          <button
            className={`add-product-submit-btn ${!isFormValid || isSubmitting ? "disabled" : ""}`}
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
          >
            {isSubmitting ? "Creating..." : "Create Product"}
          </button>
        </div>
      </div>
    </div>
  );
}

